import React, { useState } from 'react';
import { Avatar, Button, TextField, Box, Typography, IconButton, List, ListItem, ListItemAvatar } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

// Configuration constants for comment management
const EDIT_TIMEOUT_MINUTES = parseInt(process.env.REACT_APP_COMMENT_TIMEOUT_MINUTES) || 60; // Default to 60 minutes if not set
const MAX_COMMENT_LENGTH = 300; // Maximum allowed characters in a comment

/**
 * Comments Component
 * This component handles the display and management of comments in the card dialog.
 */
const Comments = ({
  currentUserId,        // ID of the currently logged-in user
  currentUserEmail,     // Email of the current user
  existingComments = [], // Array of existing comments
  onCommentAdd,         // Callback for adding a new comment
  onCommentEdit,        // Callback for editing a comment
  onCommentDelete,      // Callback for deleting a comment
  isAuthenticated       // Boolean indicating if user is logged in
}) => {
  // Local state management
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState('');

  /**
   * Formats timestamp to a consistent format: DD/MMM/YYYY, HH:MM:SS AM/PM
   */
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    
    // Split the date and time parts
    const [datePart, timePart] = formattedDate.split(', ');
    // Replace spaces with forward slashes in date part only
    return `${datePart.replace(/ /g, "/")}, ${timePart}`;
  };

  /**
   * Checks if a comment can still be edited based on its timestamp
   */
  const isCommentEditable = (timestamp) => {
    const commentTime = new Date(timestamp).getTime();
    const currentTime = new Date().getTime();
    const timeDifferenceMinutes = (currentTime - commentTime) / (1000 * 60);
    return timeDifferenceMinutes <= EDIT_TIMEOUT_MINUTES;
  };

  /**
   * Checks if a comment is older than the edit timeout
   */
  const isCommentOld = (timestamp) => {
    const commentTime = new Date(timestamp).getTime();
    const currentTime = new Date().getTime();
    const timeDifferenceMinutes = (currentTime - commentTime) / (1000 * 60);
    return timeDifferenceMinutes > EDIT_TIMEOUT_MINUTES;
  };

  /**
   * Generates display information for a user
   */
  const getUserDisplayInfo = (user) => {
    const displayName = user.authorEmail || user.email || 'Anonymous';
    const initials = displayName.split('@')[0].charAt(0).toUpperCase();
    
    return {
      displayName,
      initials
    };
  };

  /**
   * Handles the addition of a new comment
   */
  const handleAddComment = () => {
    if (!isAuthenticated) return;
    if (newComment.trim().length === 0 || newComment.length > MAX_COMMENT_LENGTH) return;

    const comment = {
      id: Date.now().toString(),
      text: newComment,
      authorId: currentUserId,
      authorEmail: currentUserEmail,
      timestamp: new Date().toISOString(),
      isDeleted: false
    };

    onCommentAdd?.(comment);
    setNewComment('');
  };

  /**
   * Handles Enter key press for comment submission
   */
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleAddComment();
    }
  };

  /**
   * Initiates comment editing process
   */
  const handleEditClick = (comment) => {
    if (!isAuthenticated) return;
    if (!isCommentEditable(comment.timestamp)) {
      alert(`Comments can only be edited within ${EDIT_TIMEOUT_MINUTES} minutes of posting.`);
      return;
    }
    
    setEditingCommentId(comment.id);
    setEditingText(comment.text);
  };

  /**
   * Saves an edited comment
   */
  const handleSaveEdit = (commentId) => {
    if (!isAuthenticated) return;
    if (editingText.trim().length === 0 || editingText.length > MAX_COMMENT_LENGTH) return;

    const updatedComment = {
      commentId,
      text: editingText,
      timestamp: new Date().toISOString(),
      isEdited: true
    };

    onCommentEdit?.(commentId, updatedComment);
    setEditingCommentId(null);
    setEditingText('');
  };

  /**
   * Cancels the current edit operation
   */
  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingText('');
  };

  /**
   * Handles comment deletion
   */
  const handleDeleteComment = (commentId) => {
    if (!isAuthenticated) return;
    
    const comment = existingComments.find(c => c.id === commentId);
    if (!comment) return;
    
    if (!isCommentOld(comment.timestamp)) {
      // If comment is within timeout period, completely remove it
      onCommentDelete?.(commentId, true);
    } else {
      // If comment is older than timeout period, mark it as deleted
      onCommentDelete?.(commentId, false);
      alert(`Comments can be completely deleted within ${EDIT_TIMEOUT_MINUTES} minutes of posting. After that, they will only be marked as deleted.`);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>Comments</Typography>
      <Box>
        {isAuthenticated ? (
          <TextField
            fullWidth
            placeholder="Add a comment (Press Enter to submit)"
            variant="outlined"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyPress={handleKeyPress}
            error={newComment.length > MAX_COMMENT_LENGTH}
            helperText={newComment.length > MAX_COMMENT_LENGTH ? `Maximum ${MAX_COMMENT_LENGTH} characters` : ''}
            multiline
            rows={2}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              }
            }}
            InputProps={{
              endAdornment: (
                <Box
                  onClick={handleAddComment}
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#4e54c8',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    ml: 1,
                    '&:hover': {
                      bgcolor: '#3f44a0'
                    }
                  }}
                >
                  <AddIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
              ),
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please log in to add comments
          </Typography>
        )}
        
        <List sx={{ 
          maxHeight: 300, 
          overflow: 'auto',
          '& .MuiListItem-root': {
            px: 2,
            py: 1,
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.02)'
            }
          }
        }}>
          {existingComments
            .map((comment) => {
            const { displayName, initials } = getUserDisplayInfo(comment);
            const isEditable = isCommentEditable(comment.timestamp);
            
            return (
              <ListItem
                key={comment.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  borderBottom: '1px solid #eee'
                }}
              >
                <ListItemAvatar sx={{ minWidth: 32 }}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: '#4e54c8'
                    }}
                  >
                    {initials}
                  </Avatar>
                </ListItemAvatar>
                <Box sx={{ flex: 1 }}>
                  {editingCommentId === comment.id ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        error={editingText.length > MAX_COMMENT_LENGTH}
                        multiline
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSaveEdit(comment.id)}
                        disabled={editingText.length > MAX_COMMENT_LENGTH}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        onClick={handleCancelEdit}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 600 }}>
                          {displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          • {formatTimestamp(comment.timestamp)}
                          {comment.isEdited && ' (edited)'}
                        </Typography>
                        {comment.authorId === currentUserId && isAuthenticated && (
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                            {isEditable && !comment.isDeleted && (
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(comment)}
                                sx={{ 
                                  p: 0.5,
                                  color: '#1976d2',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  }
                                }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                            {!comment.isDeleted && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteComment(comment.id)}
                                sx={{ 
                                  p: 0.5,
                                  color: '#d32f2f',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                  }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mt: 0.5, 
                          whiteSpace: 'pre-wrap',
                          textDecoration: comment.isDeleted ? 'line-through' : 'none',
                          color: comment.isDeleted ? 'text.secondary' : 'text.primary'
                        }}
                      >
                        {comment.text}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default Comments; 